import { fmtNum } from '../lib/utils';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { kpiApi } from '../lib/api';
import MetricPicker, { resetMetricCatalogCache } from '../components/MetricPicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { auditToText } from '../lib/sourceAudit';
import SourceUsageAudit from '../components/SourceUsageAudit';

/**
 * Source Data — the plant's numbers, read inside HR, grouped the way the
 * Operations app groups its own screens.
 *
 * Operations sends machine keys and no labels: `red_tag_value_inr`,
 * `fi_pcs_per_inspector_hour`. The server auto-translates those into English
 * ("Quality → Red tag value (₹)") and anything that still reads badly is
 * renamed HERE, once — a rename is the human's column and no later pull
 * overwrites it.
 *
 * Everything on this page is evidence rather than assertion. A metric appears
 * because the last pull returned it, its per-unit figures are the figures that
 * pull carried, and a path that stopped being supplied is stamped rather than
 * quietly vanishing. That is the whole reason the catalogue exists: binding a
 * KPI should mean picking from a list known to resolve.
 */

const SRC_LABEL = { ops: 'Operations', maint: 'Machine Maintenance', hr: 'HR software', npd: 'NPD' };

// Operations' own words for how much it trusts a block. Shown verbatim — it is
// the ERP being honest about its gaps, and paraphrasing it would lose that.
const STATUS = {
  available: { text: 'Available', bg: '#E7F7EE', fg: '#15803D',
    help: 'Operations measures this directly.' },
  proxy: { text: 'Proxy', bg: '#FEF6E7', fg: '#A16207',
    // Worth spelling out: a proxy still SCORES. It is not a gap, it is a
    // measurement of something adjacent — usually the plan rather than the
    // achieved figure, because Operations records no attendance.
    help: 'A stand-in, not a direct measurement. Operations does not record this exactly, '
        + 'so it reports the closest thing it has — usually the PLANNED figure rather than '
        + 'what was actually achieved. It still produces a mark; just read it knowing that.' },
  unavailable: { text: 'No data', bg: '#F1F3F7', fg: '#64748B',
    help: 'Operations does not record this at all. A KPI bound to it can never be scored automatically.' },
  no_data: { text: 'No data', bg: '#F1F3F7', fg: '#64748B',
    help: 'Nothing reported for this plant.' },
};

function fmt(v, uom) {
  if (v === null || v === undefined) return null;
  if (typeof v !== 'number') return String(v);
  // Shared formatter: keeps 0.003 as 0.003 instead of rounding it to a flat 0.
  const n = fmtNum(v);
  if (uom === '₹') return `₹${n}`;
  if (uom === '%') return `${n}%`;
  if (uom === 'PPM') return `${n} PPM`;
  if (uom) return `${n} ${uom}`;
  return n;
}

// What the figure builder is allowed to offer. Declared at module level so it
// keeps one identity across renders — the picker holds it in a memo, and a
// fresh arrow function each render would rebuild that list on every keystroke
// somewhere else on the page.
const notDerived = (m) => !m.derived;

function Pill({ status }) {
  const s = STATUS[status] || STATUS.available;
  return (
    <span title={s.help} className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: s.bg, color: s.fg }}>{s.text}</span>
  );
}

export default function SourceData() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState('ops');
  const [q, setQ] = useState('');
  const [openBlocks, setOpenBlocks] = useState({});
  const [editing, setEditing] = useState(null);   // metric id being renamed
  // ---- Build a figure ----------------------------------------------------
  // Two numbers compared is not a new capability: `denominator_field` has
  // always meant "field ÷ denominator_field × 100" and the engine has always
  // honoured it. Exactly ONE KPI used it, because nothing ever asked. This is
  // the screen that asks.
  const [fx, setFx] = useState(null);     // {top, bottom} metric ids, null = closed
  const [fxOut, setFxOut] = useState(null);
  const [fxBusy, setFxBusy] = useState(false);

  // ---- provenance audit (read-only) ---------------------------------------
  // Pulls the trace for the source currently being viewed and hands it to the
  // AI Assistant as plain text. Nothing is written and no KPI is touched — the
  // server endpoint only reads the snapshot it already stored.
  const [auditBusy, setAuditBusy] = useState(false);

  const buildAudit = useCallback(async () => {
    const r = await kpiApi.sourceAudit({ source });
    return auditToText(r.data);
  }, [source]);

  const showAudit = useCallback(async () => {
    setAuditBusy(true);
    try {
      const text = await buildAudit();
      // The assistant panel listens for this and prints the report verbatim;
      // it is never sent to the model, so no data leaves the browser.
      window.dispatchEvent(new CustomEvent('ai-assistant:push', {
        detail: { text, title: `${SRC_LABEL[source] || source} — source audit` },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the audit');
    } finally { setAuditBusy(false); }
  }, [buildAudit]);

  const copyAudit = useCallback(async () => {
    setAuditBusy(true);
    try {
      const text = await buildAudit();
      await navigator.clipboard.writeText(text);
      toast.success(`Audit copied — ${text.split('\n').length} lines`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not copy the audit');
    } finally { setAuditBusy(false); }
  }, [buildAudit]);

  const [fxName, setFxName] = useState('');
  const [fxDept, setFxDept] = useState('');
  const [depts, setDepts] = useState([]);
  const [saving, setSaving] = useState(false);
  // ---- Uploading ----------------------------------------------------------
  // This page is where the connected software is READ, so it is also where it
  // is LOADED. Sending somebody to KPI Center to load a month, then back here
  // to look at what arrived, split one action across two screens.
  //
  // There is no month or year to pick. The file says which app wrote it and
  // which month it covers, and trusting the file removes the mistake the old
  // screen allowed: fetching August with the selector reading September and
  // storing it under the wrong one.
  const [uploading, setUploading] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  // The usage audit, open on demand. Off by default: it answers a
  // maintenance question, and this screen's first job is the figures.
  const [showUsage, setShowUsage] = useState(false);
  // The dotted field each number is read from. OFF by default: it was printed
  // under every row, which is what made this page read as a database table
  // rather than a list of figures. Still one click away, because a mapping
  // that stops resolving is diagnosed from exactly that string.
  const [showTech, setShowTech] = useState(false);
  // Show only the numbers NO KPI reads.
  //
  // The catalogue has always answered "what can this plant supply?" and never
  // "and is any of it used?". Those are different questions, and the second is
  // the one asked before building another figure or chasing a source for a
  // field: a number that arrives every month and is scored by nobody was
  // previously discoverable only by opening all 230 KPIs one at a time.
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  // A source whose figures this app WORKS OUT rather than receives. HR is the
  // only one: attrition, late coming, suggestions and training come from
  // registers kept here, so there is no month to import and no month to be
  // missing. Everything that offers an import has to know that.
  const computedSource = source === 'hr';

  const allMetrics = useMemo(
    () => (data?.blocks || []).flatMap((b) => b.metrics), [data]);
  // The builder holds PATHS, because that is what the picker returns and what
  // a KPI is bound by. It used to hold catalogue ids, which meant the one
  // screen that assembles a figure spoke a different language from everything
  // that consumes one.
  const metricByPath = useCallback(
    (path) => allMetrics.find((m) => m.path === path) || null, [allMetrics]);
  // A built figure divides two PATHS. Printing those raw under its name would
  // make the one row on this screen that a person wrote themselves the only
  // row written in machine keys, so each half is named the way it is named
  // everywhere else.
  const pathLabel = useCallback((path) => {
    const m = allMetrics.find((x) => x.path === path);
    return m ? (m.short_label || m.label) : path;
  }, [allMetrics]);
  const builtSum = useCallback((m) => {
    const d = m?.derived;
    if (!d) return null;
    return d.denominator_field
      ? `${pathLabel(d.field)} ÷ ${pathLabel(d.denominator_field)} × 100`
      : pathLabel(d.field);
  }, [pathLabel]);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await kpiApi.metricCatalog({ source, include_hidden: showHidden });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the source data');
    } finally { setBusy(false); }
  }, [source, showHidden]);

  // Departments that already exist on a KPI. Offered as a list rather than a
  // text box for the same reason sheet names are: a typo here creates a
  // department of one, which then reads as a real grouping on every screen.
  useEffect(() => {
    kpiApi.master().then((r) => {
      const s = new Set();
      (r.data?.sources || []).forEach((g) => (g.kpis || []).forEach(
        (k) => k.department && s.add(k.department)));
      setDepts([...s].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    }).catch(() => setDepts([]));
  }, []);

  // Parse in the BROWSER before sending. A document that is not JSON at all
  // fails here, naming the file, instead of halfway through ingestion as a 400
  // about a payload the person never saw.
  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setLastUpload(null);
    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      toast.error(`${file.name} is not a JSON file. Use the month export button inside the source app.`);
      setUploading(false);
      return;
    }
    try {
      const r = await kpiApi.uploadMonth({ payload: parsed, file_name: file.name });
      const d = r.data || {};
      // An import is the one thing that changes what the catalogue contains, so
      // it is the one thing that must drop the copy the KPI pickers share.
      resetMetricCatalogCache();
      setLastUpload(d);
      toast.success(
        `${d.month} ${d.fy} loaded from ${d.source_label}: ${d.metrics_catalogued ?? 0} number(s) `
        + `across ${(d.units_stored || []).length} unit(s)`);
      (d.warnings || []).forEach((w) => toast.warning(w, { duration: 8000 }));
      // Show the source the file actually belongs to, which may not be the tab
      // the person happened to be on.
      if (d.source && d.source !== source) setSource(d.source);
      else await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not load ${file.name}`);
    } finally { setUploading(false); }
  };

  // preventDefault on BOTH enter and over, or the browser treats the drop as a
  // navigation and replaces the app with the raw JSON — the file is gone from
  // the page and so is everything the person had open.
  const onDragOver = (e) => {
    if (uploading) return;
    e.preventDefault();
    setDragging(true);
  };
  // Fires when the pointer crosses onto a CHILD element too, which would make
  // the zone flicker as the cursor moves over the text inside it. Only the
  // pointer actually leaving the panel counts.
  const onDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (uploading) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    // One month per import. Dropping a folder of exports and having the last
    // one silently win is worse than being told to do them one at a time.
    if (files.length > 1) {
      toast.error('Drop one month at a time — each file is imported on its own.');
      return;
    }
    uploadFile(files[0]);
  };

  const saveAsKpi = async () => {
    const top = metricByPath(fx?.top);
    const bottom = fx?.bottom ? metricByPath(fx.bottom) : null;
    if (!top || !fxName.trim()) return;
    setSaving(true);
    try {
      // The figure goes into the CATALOGUE first, because that is the thing
      // this screen is a view of. Building a figure used to produce a KPI and
      // nothing else, so the number somebody had just assembled was missing
      // from the very list they assembled it on.
      //
      // Only a RATIO is published. Picking one number and reading it as-is
      // creates no new figure — that row is already below, and filing a second
      // copy under a different name would put the same number in the picker
      // twice.
      if (bottom) {
        await kpiApi.buildMetric({
          name: fxName.trim(), source,
          field: top.path, denominator_field: bottom.path, uom: '%',
        });
        resetMetricCatalogCache();
      }
      await kpiApi.saveMaster({
        // The key is derived from the name and never changes afterwards —
        // it is what binds templates, sheets and marks to this KPI.
        key: fxName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
        name: fxName.trim(),
        source: source === 'ops' ? 'operations' : source === 'maint' ? 'maintenance' : 'hr',
        department: fxDept || '',
        field: top.path,
        denominator_field: bottom ? bottom.path : null,
        uom: bottom ? '%' : (top.uom || ''),
        direction: 'higher',
        objective: '', formula: '', criteria: '',
        // No ladder yet — deliberately. It is set in KPI Center where the
        // editor can try real values against it, and the KPI shows as
        // "NO SCORING RULE" until then rather than pretending to be ready.
        bands: null, target: null, active: true,
      });
      toast.success(bottom
        ? `"${fxName.trim()}" created — it is in the list below under Built figures, `
          + 'and its marking ladder is set in KPI Center'
        : `"${fxName.trim()}" created — it reads ${top.short_label || top.label}, `
          + 'already in the list below. Set its marking ladder in KPI Center');
      setFxName(''); setFxDept('');
      // Show the figure that was just built, rather than asking somebody to
      // press Refresh to find out whether it worked.
      if (bottom) await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the KPI');
    } finally { setSaving(false); }
  };
  useEffect(() => { load(); }, [load]);

  // Search runs in the browser so typing stays instant. The server takes the
  // same `q` for anything that needs it server-side.
  //
  // "Not linked" narrows the same list rather than being a screen of its own:
  // the question is about THESE numbers, and answering it somewhere else would
  // mean reading two lists to find out whether one row is used.
  const blocks = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.blocks || [])
      .map((b) => {
        const metrics = b.metrics.filter((m) => {
          if (onlyUnlinked && (m.read_by || []).length) return false;
          if (!needle) return true;
          return m.label.toLowerCase().includes(needle)
              || (m.path || '').toLowerCase().includes(needle);
        });
        // Counted over what SURVIVED the filter, never carried through from
        // the server's figure for the whole block. Those two disagree the
        // moment anything is filtered, and the header then reads "133 of 108
        // have a figure" — a total smaller than its own part.
        return { ...b, metrics,
                 count: metrics.length,
                 with_values: metrics.filter((m) => m.has_any_value).length,
                 unlinked: metrics.filter((m) => !(m.read_by || []).length).length };
      })
      .filter((b) => b.metrics.length);
  }, [data, q, onlyUnlinked]);

  // Every unit any metric reported, so the value columns line up across blocks.
  const units = useMemo(() => {
    const s = new Set();
    (data?.blocks || []).forEach((b) => b.metrics.forEach(
      (m) => Object.keys(m.units || {}).forEach((u) => s.add(u))));
    return [...s].filter((u) => u !== '__company__').sort();
  }, [data]);

  // Hide a number nobody should bind a KPI to. NOT a delete: the catalogue
  // records what the imported file contained, so a removed row would simply
  // come back on the next import. Hiding takes it out of the picker and leaves
  // the record intact.
  const setHidden = async (m, hidden) => {
    try {
      await kpiApi.renameMetric(m.id, { active: !hidden });
      resetMetricCatalogCache();
      toast.success(hidden
        ? `"${m.short_label || m.label}" hidden — it will not be offered when binding a KPI`
        : `"${m.short_label || m.label}" is available again`);
      load();
    } catch (e) {
      // The server refuses to hide a number a KPI is reading, and names them.
      toast.error(e?.response?.data?.detail || 'Could not hide this number', { duration: 9000 });
    }
  };

  const saveName = async (m) => {
    const name = draft.trim();
    try {
      const r = await kpiApi.renameMetric(m.id, { label_override: name });
      toast.success(name ? `Renamed to "${r.data.label}"` : `Reset to "${r.data.label}"`);
      setEditing(null); setDraft('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not rename'); }
  };

  const totalShown = blocks.reduce((n, b) => n + b.metrics.length, 0);

  // Recompute whenever either side changes. Debounced only lightly — this is a
  // two-dropdown form, not a keystroke.
  useEffect(() => {
    if (!fx?.top) { setFxOut(null); return; }
    const top = metricByPath(fx.top);
    const bottom = fx.bottom ? metricByPath(fx.bottom) : null;
    if (!top) { setFxOut(null); return; }
    let cancelled = false;
    setFxBusy(true);
    kpiApi.previewFormula({
      source: source.toUpperCase(),
      field: top.path,
      denominator_field: bottom ? bottom.path : null,
      uom: bottom ? '%' : (top.uom || ''),
    }).then((r) => { if (!cancelled) setFxOut(r.data); })
      .catch((e) => {
        if (!cancelled) { setFxOut(null); toast.error(e?.response?.data?.detail || 'Could not work that out'); }
      })
      .finally(() => { if (!cancelled) setFxBusy(false); });
    return () => { cancelled = true; };
  }, [fx, source, metricByPath]);

  // Rendered as a KPI Center tab, not a page of its own. These numbers exist
  // for one purpose — to be pointed at by a KPI — and on a separate page they
  // read as a reference list nobody had a reason to open. Beside the KPIs they
  // feed, they are the answer to "what can this KPI actually read?".
  const body = (
    <>
      <div className="flex items-baseline gap-2 flex-wrap">
        <h2 className="text-lg font-bold text-[#1E293B]">Source Data</h2>
        <p className="text-sm text-[#64748B]">
          View and verify numbers imported from connected systems.
        </p>
        {/* The paragraph that stood here explained what the list is FOR — that
            a KPI can only read what appears on it. True, and worth knowing
            once; not worth four lines above every visit. */}
        <span className="text-[#CBD5E1] cursor-help text-xs" data-testid="src-help"
          title={'These are the numbers a KPI can be pointed at. If a number is not on '
               + 'this list, nothing can be scored from it automatically.'}>ⓘ</span>
      </div>

          {/* Above the source tabs, because it is not tied to one. The file
              names its own app and month, so importing is a single action that
              happens before you choose what to look at — not a thing you do
              "to" whichever tab you happen to be on. */}
          <div className="rounded-xl border border-[#E2E8F0] bg-white">
            {/* Source, month and import on ONE line. These three answer the
                same question — "what am I looking at, and is it loaded?" — and
                as three stacked blocks they read as three separate concerns.
                The drop zone that used to sit here was 120px of empty space
                above every visit; it is now the strip itself, which still
                accepts a drop and still opens a file picker on click. */}
            <label
              onDragEnter={computedSource ? undefined : onDragOver}
              onDragOver={computedSource ? undefined : onDragOver}
              onDragLeave={computedSource ? undefined : onDragLeave}
              onDrop={computedSource ? undefined : onDrop}
              data-testid="src-upload-label"
              className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 rounded-xl transition-colors
                ${computedSource ? 'cursor-default'
                  : uploading ? 'cursor-wait bg-[#F1F5F9]'
                  : dragging ? 'cursor-copy bg-[#EEF3FF] ring-2 ring-inset ring-[#2563EB]'
                  : 'cursor-pointer hover:bg-[#FAFBFC]'}`}>
              {/* Omitted entirely for HR. Left in place it would still open a
                  file picker on any click of this strip, inviting somebody to
                  import a month for the one source that never has one. */}
              {!computedSource && (
                <input type="file" accept=".json,application/json" className="hidden"
                  disabled={uploading} data-testid="src-upload"
                  onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ''; }} />
              )}

              {/* Which source you are LOOKING at. Never what you are loading —
                  the file states its own app and month, and the server reads
                  both, so there is nothing here to pick wrongly. */}
              <div className="flex items-center gap-1 rounded-lg bg-[#F1F5F9] p-0.5"
                onClick={(e) => e.preventDefault()}>
                {/* HR is listed with the rest because the question — "what
                    numbers can a KPI read?" — has one answer, not two. It is
                    the only source with nothing to import: its figures are
                    computed here from this app's own registers. */}
                {['ops', 'maint', 'npd', 'hr'].map((s) => (
                  <button key={s} type="button" data-testid={`src-tab-${s}`}
                    onClick={(e) => { e.preventDefault(); setSource(s); }}
                    className={`rounded-md px-3 py-1 text-[13px] font-medium transition-colors ${
                      source === s ? 'bg-white text-[#1E293B] shadow-sm'
                        : 'text-[#64748B] hover:text-[#1E293B]'}`}>
                    {SRC_LABEL[s]}
                    {data?.by_source?.[s] != null && source === s
                      ? <span className="text-[#94A3B8] font-normal"> · {data.by_source[s]}</span> : ''}
                  </button>
                ))}
              </div>

              {/* The month, stated once, where the source is chosen. */}
              {data?.period ? (
                <span className="flex items-center gap-1.5 text-[13px]"
                  data-testid="src-period">
                  <span className={data.period.month_to_date ? 'text-[#A16207]' : 'text-[#15803D]'}>
                    {data.period.month_to_date ? '◐' : '✓'}
                  </span>
                  <b className="text-[#1E293B]">{data.period.label}</b>
                  <span className="text-[#64748B]">
                    {data.period.month_to_date
                      ? `part month — worked out ${data.period.generated_at
                          ? new Date(data.period.generated_at).toLocaleDateString('en-IN',
                              { day: 'numeric', month: 'short' }) : 'mid-month'}`
                      : 'complete month'}
                  </span>
                  {data.period.pulled_at && (
                    <span className="text-[#94A3B8]">
                      · loaded {new Date(data.period.pulled_at).toLocaleDateString('en-IN',
                        { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-[13px] text-[#94A3B8]" data-testid="src-period">
                  No month loaded for {SRC_LABEL[source]}
                </span>
              )}

              <span className={`ml-auto flex items-center gap-2 text-[13px] ${computedSource
                ? 'text-[#64748B]' : 'font-semibold text-[#2563EB]'}`}>
                {computedSource ? 'Computed here — nothing to import'
                  : uploading ? 'Reading the file…'
                  : dragging ? 'Drop it to import'
                  : <>⭱ Import / replace file</>}
              </span>
            </label>

            {/* What the upload actually did. Worth showing rather than a toast
                that disappears: which plants were stored is the thing somebody
                checks, and a plant SKIPPED is the thing they would otherwise
                never learn about. */}
            {lastUpload && (
              <div className="mt-2 border-t border-[#E2E8F0] pt-2 text-[12px] text-[#475569]"
                data-testid="src-upload-result">
                <b>{lastUpload.file_name}</b> · {lastUpload.source_label} ·{' '}
                {lastUpload.period || `${lastUpload.month} ${lastUpload.fy}`}
                {lastUpload.generated_by && <> · exported by {lastUpload.generated_by}</>}
                <div className="mt-1">
                  Stored for <b>{(lastUpload.units_stored || []).join(', ') || 'no unit'}</b>
                  {' · '}{lastUpload.metrics_catalogued} number(s)
                  {lastUpload.metrics_now_missing
                    ? ` · ${lastUpload.metrics_now_missing} no longer supplied` : ''}
                </div>
                {(lastUpload.units_skipped || []).length > 0 && (
                  <div className="mt-1 text-[#A16207]">
                    Skipped <b>{lastUpload.units_skipped.join(', ')}</b> — no unit in this app
                    claims that code. Add it to a unit's source aliases in Settings if it
                    should be scored.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* The measurements toolbar. Previously everything — source tabs,
              search, Build a figure, Show hidden, Refresh and two audit
              buttons — sat on one line with equal weight, so nothing read as
              primary and the tabs competed with the controls. Source moved up
              beside the month; what is left here acts on the LIST below it,
              and the three occasional actions are text links rather than
              buttons of the same size as search. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-1">
            <h3 className="text-sm font-bold text-[#1E293B]">Measurements</h3>
            {!!blocks.length && (
              <span className="text-[12px] text-[#64748B]" data-testid="src-count">
                {blocks.reduce((n, b) => n + b.with_values, 0)} of {totalShown} have a figure
                {data?.missing ? ` · ${data.missing} no longer supplied` : ''}
              </span>
            )}
            {/* Which of these numbers nothing in the KPI Master reads. A
                toggle rather than a link to another screen: the answer is
                about THIS list, and sending somebody elsewhere to get it means
                reading two lists to learn whether one row is used. */}
            {!!data?.unlinked && (
              <button onClick={() => setOnlyUnlinked((v) => !v)} data-testid="src-unlinked"
                aria-pressed={onlyUnlinked}
                title={'Show only the numbers no KPI reads. These still arrive every month '
                  + 'and score nobody — either point a KPI at one, or hide it so it stops '
                  + 'being offered.'}
                className={`rounded-full px-2.5 py-1 text-[12px] font-medium border transition-colors ${
                  onlyUnlinked
                    ? 'bg-[#2563EB] border-[#2563EB] text-white'
                    : 'bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#1E293B]'}`}>
                {data.unlinked} not linked
              </button>
            )}
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="src-search"
              placeholder="Search measurements…" className="w-56 ml-auto h-9 text-sm" />
            <label className="flex items-center gap-1.5 text-[12px] text-[#64748B] cursor-pointer"
              title="Show the exact field each number is read from">
              <input type="checkbox" checked={showTech} data-testid="src-show-tech"
                onChange={(e) => setShowTech(e.target.checked)} />
              Technical fields
            </label>
            {/* Hidden numbers are out of the way, not gone. Without a way back
                to them, hiding one by mistake would be unrecoverable from the
                screen that did it. */}
            <label className="flex items-center gap-1.5 text-[12px] text-[#64748B] cursor-pointer"
              title={'Show numbers somebody has hidden, so they can be restored — and the '
                + 'second copies of figures the source publishes under two different block names.'}>
              <input type="checkbox" checked={showHidden} data-testid="src-show-hidden"
                onChange={(e) => setShowHidden(e.target.checked)} />
              Show hidden
            </label>
            <span className="flex items-center gap-3 text-[12px]">
              <button onClick={() => { setFx(fx ? null : { top: '', bottom: '' }); setFxOut(null); }}
                data-testid="src-build-open" className="text-[#2563EB] hover:underline font-medium">
                {fx ? 'Close builder' : 'Build a figure'}
              </button>
              {/* Which of these numbers a KPI actually scores. Read-only, and
                  it belongs here: this is the screen that lists the numbers,
                  so "and which of them is anybody marked on" is the next
                  question rather than a separate errand. */}
              <button onClick={() => setShowUsage((v) => !v)}
                data-testid="src-usage-open" className="text-[#2563EB] hover:underline font-medium">
                {showUsage ? 'Close usage' : 'Scored or not'}
              </button>
              <button onClick={load} disabled={busy} data-testid="src-refresh"
                className="text-[#64748B] hover:text-[#1E293B] disabled:opacity-50">
                {busy ? 'Loading…' : 'Refresh'}
              </button>
              {/* Audit / traceability only — reads the stored snapshot back and
                  reports where every figure came from. Writes nothing. */}
              <button onClick={showAudit} disabled={auditBusy} data-testid="src-show-audit"
                className="text-[#64748B] hover:text-[#1E293B] disabled:opacity-50">
                {auditBusy ? 'Building…' : 'Audit'}
              </button>
              <button onClick={copyAudit} disabled={auditBusy} data-testid="src-copy-audit"
                className="text-[#64748B] hover:text-[#1E293B] disabled:opacity-50">
                Copy
              </button>
            </span>
          </div>


          {/* Compare one number against another, without writing anything.
              The result is computed by the SAME function that computes the real
              KPI (`resolve_master_kpi`) — a second copy of "divide and times a
              hundred" in the browser is how a preview comes to disagree with
              the mark, so there is none. */}
          {fx && (
            <div className="rounded-xl border border-[#C7D7FE] bg-[#F5F8FF] p-4" data-testid="src-build">
              <h2 className="text-sm font-bold text-[#1E293B] mb-1">Build a figure</h2>
              <p className="text-xs text-[#64748B] mb-3">
                Pick one number to read it as-is, or two to compare them as a percentage.
                The result below is this month's real data, worked out the same way a KPI would.
              </p>

              <div className="grid gap-3 md:grid-cols-2 mb-3">
                {[['top', 'Top number', 'The figure being measured'],
                  ['bottom', 'Bottom number (optional)', 'Compare the top against this, as a %']]
                  .map(([slot, label, hint]) => (
                  <div key={slot}>
                    <label className="block text-[11px] uppercase text-[#94A3B8] mb-1">
                      {label} <span className="normal-case">— {hint}</span>
                    </label>
                    {/* The same searchable picker the KPI editor binds with,
                        not a second one written here. It was a plain dropdown
                        of 130 options in source order, which is unusable for
                        the one job it has: you cannot find "inspected qty"
                        without reading past a hundred names you did not want.
                        The picker types-to-filter, groups by section, and shows
                        each number's real per-plant figure — which is what
                        actually tells you the row is the one you meant. */}
                    <div data-testid={`src-build-${slot}`}>
                      <MetricPicker source={source} value={fx[slot] || ''}
                        placeholder={slot === 'bottom'
                          ? 'None — read the top number as-is'
                          : 'Choose a number…'}
                        // Only numbers the SOURCE supplies. A figure already
                        // built here is two of these divided, and the resolver
                        // reads paths out of the imported file — so one built on
                        // another would resolve to nothing every month.
                        filter={notDerived}
                        onChange={(path) => setFx((s) => ({ ...s, [slot]: path }))} />
                    </div>
                  </div>
                ))}
              </div>

              {fxBusy && <p className="text-xs text-[#94A3B8]">Working it out…</p>}

              {fxOut && !fxBusy && (
                <div className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden">
                  <div className="px-3 py-2 border-b border-[#F1F5F9] flex items-baseline gap-2 flex-wrap">
                    <span className="text-[11px] uppercase text-[#94A3B8]">Result</span>
                    <span className="text-xs text-[#475569]">{fxOut.period}</span>
                    {fxOut.month_to_date && (
                      <span className="text-[11px] text-[#8A5A12]">part month so far</span>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {fxOut.rows.map((r) => (
                        <tr key={r.unit} className="border-b border-[#F1F5F9] last:border-0"
                          data-testid={`src-build-row-${r.unit}`}>
                          <td className="px-3 py-2 text-[#475569]">{r.unit}</td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums text-[#1E293B]">
                            {r.value === null || r.value === undefined
                              ? <span className="font-normal text-[#CBD5E1]" title={r.reason || ''}>
                                  — {r.reason ? `(${r.reason})` : ''}
                                </span>
                              : fmt(r.value, fxOut.is_ratio ? '%' : (metricByPath(fx.top)?.uom || ''))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="px-3 py-2 text-[11px] font-mono text-[#CBD5E1] border-t border-[#F1F5F9]">
                    {fxOut.formula}
                  </p>
                </div>
              )}

              {/* Turn the figure into a KPI. Deliberately WITHOUT a marking
                  ladder: that is set in KPI Center, where the editor can try
                  real values against it. Until then the KPI shows as
                  "NO SCORING RULE" rather than looking ready and scoring
                  nothing. */}
              {fxOut && !fxBusy && (
                <div className="mt-3 pt-3 border-t border-[#C7D7FE]">
                  <div className="flex items-end gap-2 flex-wrap">
                    <div>
                      <label className="block text-[11px] uppercase text-[#94A3B8] mb-1">
                        Call this KPI
                      </label>
                      <Input value={fxName} onChange={(e) => setFxName(e.target.value)}
                        placeholder="e.g. Press Shop Efficiency" data-testid="src-build-name"
                        className="w-64 text-sm" />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase text-[#94A3B8] mb-1">Department</label>
                      <select value={fxDept} onChange={(e) => setFxDept(e.target.value)}
                        data-testid="src-build-dept"
                        className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm bg-white w-52">
                        <option value="">— none —</option>
                        {depts.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <Button onClick={saveAsKpi} disabled={saving || !fxName.trim()}
                      data-testid="src-build-save">
                      {saving ? 'Creating…' : 'Create this KPI'}
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#94A3B8] mt-2">
                    It is created without a marking ladder — set what earns 4 marks in
                    KPI Center, where you can try a real value before saving.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* The month moved up beside the source, where it belongs — it is
              part of "what am I looking at", not a banner over the list. The
              part-month warning survives the move: a figure worked out on the
              21st is not a closing one, and that must stay visible. */}

          {data?.scoped_to_unit && (
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 text-sm text-[#475569]"
              data-testid="src-unit-scope">
              Showing <b>{data.scoped_to_unit}</b> only — that is the plant your role covers.
            </div>
          )}

          {/* Which plants the columns below belong to, said once. Together with
              the note about supplier sites that are left out — a missing column
              is otherwise indistinguishable from a missing figure. */}
          {(!!units.length || !!(data?.hidden_units || []).length) && (
            <p className="text-[12px] text-[#64748B]" data-testid="src-units-line">
              {!!units.length && <>Units: <b className="text-[#475569]">{units.join(' · ')}</b></>}
              {!!(data?.hidden_units || []).length && (
                <span className="text-[#94A3B8]" data-testid="src-hidden-units">
                  {units.length ? '  ·  ' : ''}Not shown: {data.hidden_units.join(', ')} —
                  reported by the source but not AAMPL units.
                </span>
              )}
            </p>
          )}

          {data && !blocks.length && !busy && (
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-6 text-sm text-[#64748B]"
              data-testid="src-empty">
              {onlyUnlinked
                ? <>
                    Every number {q ? <>matching “{q}” </> : ''}on this list is already
                    read by a KPI.{' '}
                    <button onClick={() => setOnlyUnlinked(false)}
                      data-testid="src-unlinked-clear"
                      className="text-[#2563EB] hover:underline">Show all of them</button>.
                  </>
                : q ? <>Nothing matches “{q}”.</>
                : computedSource ? <>
                    Nothing from {SRC_LABEL[source]} yet. These figures are worked out
                    from this app's own registers, so they appear as those registers
                    are filled in — there is nothing to import here.
                  </>
                : <>
                    Nothing from {SRC_LABEL[source]} yet. Export a month from it and
                    drop the file in <b>Import file</b> above — this list then fills
                    itself from whatever that file turned out to contain, so there is
                    nothing to configure here first.
                  </>}
            </div>
          )}

          {/* The count moved into the Measurements heading, where it reads as
              a status rather than as a sentence under the list. */}

          {/* The usage audit takes the page when it is open: it is a different
              question about the same numbers, and showing both lists at once
              would leave two tables of paths on screen with nothing saying
              which one answered what. */}
          {showUsage && (
            <div className="rounded-2xl border border-[#E6E9F0] bg-white p-4 mb-4">
              <SourceUsageAudit source={source} />
            </div>
          )}

          {!showUsage && blocks.map((b) => {
            const key = `${b.source}.${b.block}`;
            const open = openBlocks[key] ?? true;
            return (
              <section key={key} className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden"
                data-testid={`src-block-${b.block}`}>
                <button onClick={() => setOpenBlocks((o) => ({ ...o, [key]: !open }))}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F8FAFC]">
                  <span className="text-[#94A3B8] text-xs">{open ? '▾' : '▸'}</span>
                  {/* Machine Maintenance puts everything under one `data` key,
                      which the labeller calls "Measurements" — the same word as
                      the heading directly above it. A catch-all block has no
                      name of its own worth printing, so it takes the source's
                      instead. Sources with real sections (Operations has 13)
                      are untouched. */}
                  <span className="font-bold text-[#1E293B]">
                    {b.block === 'data' ? (SRC_LABEL[b.source] || b.block_label) : b.block_label}
                  </span>
                  {b.block_status && <Pill status={b.block_status} />}
                  <span className="text-xs text-[#94A3B8] ml-auto">
                    {b.with_values} of {b.count} have a figure
                    {/* Said on the closed header too, so a block nothing reads
                        does not have to be opened to find that out. Suppressed
                        while the filter is on, where every row is unlinked by
                        definition and the count would only repeat itself. */}
                    {!onlyUnlinked && !!b.unlinked && ` · ${b.unlinked} not linked`}
                  </span>
                </button>

                {/* Operations' own sentence explaining where the block's numbers
                    come from — or why there are none. Quoted, not paraphrased. */}
                {open && b.note && (
                  <p className="px-4 pb-2 -mt-1 text-xs text-[#64748B] italic"
                    data-testid={`src-note-${b.block}`}>{b.note}</p>
                )}

                {open && (
                  <div className="overflow-x-auto border-t border-[#F1F5F9]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#F8FAFC] text-[#64748B] text-xs">
                          <th className="text-left font-semibold px-4 py-2 min-w-[300px]">Number</th>
                          {units.map((u) => (
                            <th key={u} className="text-right font-semibold px-3 py-2 whitespace-nowrap">{u}</th>
                          ))}
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {b.metrics.map((m) => (
                          <tr key={m.id} className="border-t border-[#F1F5F9] hover:bg-[#FAFBFC]"
                            data-testid={`src-metric-${m.id}`}>
                            <td className="px-4 py-2">
                              {editing === m.id ? (
                                <div className="flex items-center gap-2">
                                  <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveName(m);
                                      if (e.key === 'Escape') { setEditing(null); setDraft(''); }
                                    }}
                                    data-testid={`src-rename-input`}
                                    placeholder={m.auto_label} className="h-8 text-sm" />
                                  <Button size="sm" onClick={() => saveName(m)}
                                    data-testid="src-rename-save">Save</Button>
                                  <Button size="sm" variant="ghost"
                                    onClick={() => { setEditing(null); setDraft(''); }}>Cancel</Button>
                                </div>
                              ) : (
                                <div>
                                  {/* The section heading already says "Quality",
                                      so the row says what distinguishes it. */}
                                  <button className="text-left text-[#1E293B] hover:text-[#1E3A8A] hover:underline"
                                    title={`${m.label}\nReads: ${m.path}\n\nClick to rename`}
                                    onClick={() => { setEditing(m.id); setDraft(m.label_override || ''); }}>
                                    {m.short_label || m.label}
                                  </button>
                                  {m.renamed && (
                                    <span className="ml-2 text-[11px] text-[#94A3B8]"
                                      title={`Automatic name: ${m.auto_label}`}>renamed</span>
                                  )}
                                  {/* Operations publishes some figures twice,
                                      under two block names — its own payload
                                      says tool_breakdown is "the same engine as
                                      the maintenance block", and the numbers
                                      agree to the decimal. The browser keeps
                                      ONE row, because two rows read as two
                                      claims about the plant and invite two KPIs
                                      onto a single number. Named, not silently
                                      dropped: somebody who knows the figure by
                                      its other path has to be able to find it. */}
                                  {!!(m.also_published_as || []).length && (
                                    <span className="ml-2 text-[11px] text-[#94A3B8]"
                                      data-testid={`src-folded-${m.id}`}
                                      title={'The source sends this same number again as:\n  '
                                        + m.also_published_as.join('\n  ')
                                        + '\n\nShown once. Tick "Show hidden" to see the other copies.'}>
                                      also sent as {m.also_published_as.length === 1
                                        ? m.also_published_as[0].split('.')[0]
                                        : `${m.also_published_as.length} other names`}
                                    </span>
                                  )}
                                  {m.duplicate_of && (
                                    <span className="ml-2 rounded px-1.5 py-0.5 text-[11px] font-semibold"
                                      style={{ background: '#F1F3F7', color: '#64748B' }}
                                      data-testid={`src-duplicate-${m.id}`}
                                      title={`The same number is already listed as ${m.duplicate_of}. `
                                        + 'Shown here only because "Show hidden" is ticked.'}>
                                      duplicate of {m.duplicate_of}
                                    </span>
                                  )}
                                  {/* A built figure is the one row here nobody
                                      imported, so it says what it is made of.
                                      Without the sum it is indistinguishable
                                      from a number the source supplied — which
                                      is exactly the thing a reader must not get
                                      wrong when a figure is disputed. */}
                                  {m.derived && (
                                    <span className="ml-2 rounded px-1.5 py-0.5 text-[11px] font-semibold"
                                      style={{ background: '#EEF3FF', color: '#1E3A8A' }}
                                      title="Worked out in HR from two of this source's numbers, not supplied by it.">
                                      built here
                                    </span>
                                  )}
                                  {m.active === false ? (
                                    <>
                                      <span className="ml-2 rounded px-1.5 py-0.5 text-[11px] font-semibold"
                                        style={{ background: '#F1F3F7', color: '#64748B' }}>hidden</span>
                                      <button onClick={() => setHidden(m, false)}
                                        data-testid={`src-unhide-${m.id}`}
                                        className="ml-2 text-[11px] text-[#2563EB] hover:underline">restore</button>
                                    </>
                                  ) : (
                                    <button onClick={() => setHidden(m, true)}
                                      data-testid={`src-hide-${m.id}`}
                                      title="Take this out of the list offered when binding a KPI. The number is kept; it is just not offered."
                                      className="ml-2 text-[11px] text-[#CBD5E1] hover:text-[#C2151F]">hide</button>
                                  )}
                                  {m.missing_since && (
                                    <span className="ml-2 rounded px-1.5 py-0.5 text-[11px] font-semibold"
                                      style={{ background: '#FDE9EF', color: '#C2151F' }}
                                      title={`Last supplied ${new Date(m.missing_since).toLocaleDateString('en-IN')}. A KPI pointed at this will not score.`}
                                      data-testid={`src-missing-${m.id}`}>
                                      no longer supplied
                                    </span>
                                  )}
                                  {/* The field this number is read from. Behind
                                      the "Technical fields" toggle, because
                                      printing it under all 130 names is what
                                      made the page read as a database table —
                                      and it answers a question an HR user
                                      never asks. The title keeps it reachable
                                      on hover even while hidden. */}
                                  {m.derived && (
                                    <div className="text-[11px] text-[#64748B]"
                                      data-testid={`src-built-sum-${m.id}`}>{builtSum(m)}</div>
                                  )}
                                  {/* Which KPIs read this number. The answer to
                                      "can I hide this?" and to "where does that
                                      mark come from?", and until now it was
                                      only obtainable by opening all 230 KPIs
                                      one at a time. Printed only when something
                                      DOES read it: most of the catalogue is
                                      unread, so a "not linked" line under a
                                      hundred rows would be noise — that is what
                                      the filter above is for.

                                      NAMED, not counted. "read by 2 KPIs" is a
                                      number somebody then has to go and
                                      reconcile against KPI Master — and the
                                      search there matches the KPI's own name,
                                      so a second reader called something else
                                      cannot be found from the count at all.
                                      The names are the answer; the count on
                                      its own is the question. */}
                                  {!!(m.read_by || []).length && (
                                    <div className="text-[11px] text-[#15803D]"
                                      data-testid={`src-read-by-${m.id}`}
                                      title={`Read by:\n  ${m.read_by.join('\n  ')}`}>
                                      read by {m.read_by.slice(0, 3).join(', ')}
                                      {m.read_by.length > 3
                                        && ` + ${m.read_by.length - 3} more`}
                                    </div>
                                  )}
                                  {showTech && (
                                    <div className="text-[11px] text-[#CBD5E1] font-mono">
                                      {m.derived ? m.formula : m.path}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            {units.map((u) => {
                              const cell = (m.units || {})[u];
                              const shown = cell ? fmt(cell.value, m.uom) : null;
                              return (
                                <td key={u} className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                                  {shown !== null && shown !== undefined
                                    ? <span className="text-[#1E293B] font-medium">{shown}</span>
                                    : <span className="text-[#CBD5E1]" title={
                                        cell ? 'This plant reported nothing for this number.'
                                             : 'This plant was not in the last pull.'}>—</span>}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2 text-right">
                              {m.status && m.status !== 'available' && <Pill status={m.status} />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
      })}
    </>
  );

  return <div className="space-y-5" data-testid="source-data-page">{body}</div>;
}
